/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { createObservableDataAction } from '@msdyn365-commerce/action-internal';
import { getCatalogIdForSdk, IAction, IActionContext, ICookieContext, versionGte } from '@msdyn365-commerce/core-internal';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    ProductSearchCriteria,
    ProductSearchResult,
    QueryResultSettings
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getProductImageUrls } from '../../utils/product-list-utils';
import { ProductListInput } from '../hydrator-action-inputs';
import { RecentlyViewedProductItem } from './base';
import {
    getInventorySorting,
    getInventoryInStockSortableRefinerValue,
    ProductListInventoryFilteringOptions
} from '../utils/inventory-util';

export const getProductsByRecentlyViewedList = async (input: ProductListInput, ctx: IActionContext): Promise<ProductSearchResult[]> => {
    const searchCriteriaInput: ProductSearchCriteria = {};
    const cookieContext: ICookieContext = ctx.requestContext.cookies;
    const catalogId = getCatalogIdForSdk(ctx.requestContext, null);
    searchCriteriaInput.Context = {
        ChannelId: ctx.requestContext.apiSettings.channelId,
        CatalogId: catalogId
    };
    searchCriteriaInput.IncludeAttributes = false;
    searchCriteriaInput.SkipVariantExpansion = true;
    const cookieName: string = '_msdyn365___recently_viewed_products';
    const cookieValue = cookieContext.get<RecentlyViewedProductItem[] | undefined | null>(cookieName).value;
    const productIds = cookieValue?.filter(value => value.catalogId === catalogId).map(value => value.productId) || [0];
    searchCriteriaInput.Ids = productIds;

    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: input.listMetadata.pageSize || 10
        }
    };

    const rsVersion =
        !process.env.MSDyn365Commerce_RSVERSION || process.env.MSDyn365Commerce_RSVERSION === '--'
            ? '0.0'
            : process.env.MSDyn365Commerce_RSVERSION;
    const isHideAndSortByInventoryAvailabilitySupported = versionGte(rsVersion, '9.37');
    if (isHideAndSortByInventoryAvailabilitySupported) {
        let channelInventoryConfiguration = {};
        if (
            ctx.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS ||
            ctx.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.SortOOS
        ) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
            const StoreOperationsDataActions = require('@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g');
            channelInventoryConfiguration = await StoreOperationsDataActions.getInventoryConfigurationAsync({ callerContext: ctx });
        }

        if (ctx.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS) {
            const inventoryInStockRefinerValue = getInventoryInStockSortableRefinerValue(channelInventoryConfiguration);
            if (inventoryInStockRefinerValue) {
                searchCriteriaInput.Refinement = [inventoryInStockRefinerValue];
            }
        }

        if (ctx.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.SortOOS) {
            const sorting = getInventorySorting(channelInventoryConfiguration);
            if (sorting) {
                queryResultSettings.Sorting = sorting;
            }
        }
    }

    return searchByCriteriaAsync(
        {
            callerContext: ctx,
            queryResultSettings
        },
        searchCriteriaInput
    )
        .then((products: ProductSearchResult[]) => {
            ctx.telemetry.debug('Products returned by ProductByRecentlyViewed action', products);
            return getProductImageUrls(products, ctx.requestContext.apiSettings);
        })
        .catch(error => {
            ctx.telemetry.error(`Error running ProductByRecentlyViewed action: ${error}`);
            throw new Error(error);
        });
};

export const getProductsByRecentlyViewedListAction = createObservableDataAction({
    action: <IAction<ProductSearchResult[]>>getProductsByRecentlyViewedList,
    id: '@msdyn365-commerce/products-by-recently-viewed'
});
